import { Component, Event, EventEmitter, h, Prop, State, Element } from '@stencil/core';
import { getConstant } from '../../../utils/constants-helpers';

/**
 * Input component with validation and error handling capabilities
 *
 * Uses constants from constants-provider for validation messages:
 * @constant TEXT_FORM_MESSAGE_FIELD_REQUIRED - "This field is required" - Message shown when a required field is empty
 * @constant TEXT_FORM_MESSAGE_EMAIL_REQUIRED - "Please enter your Email" - Message shown when a required email field is empty
 * @constant TEXT_FORM_MESSAGE_FIELD_IS_INVALID - "Invalid value" - Message shown when a field contains invalid data
 * @constant TEXT_FORM_MESSAGE_EMAIL_INVALID - "This value seems to be invalid" - Message shown when email field contains invalid email address
 */
@Component({
  tag: 'me-input',
  styleUrl: 'index.scss',
  shadow: false,
})
export class Input {
  /**
   * Component constants object containing all required text constants
   */
  @Prop() constants?: Record<string, string>;

  @Prop({ reflect: true }) name: string = '';
  @Prop() placeholder: string = '';
  @Prop({ mutable: true, reflect: true }) value: string = '';
  @Prop() type: string = 'text';
  @Prop() disabled: boolean = false;
  @Prop() required: boolean = false;
  @Prop() label?: string;

  /**
   * Custom message for required error
   */
  @Prop() requiredMessage?: string;

  /**
   * Custom message for invalid error
   */
  @Prop() invalidMessage?: string;

  /**
   * Error type: 'required' | 'invalid' (set by form handler)
   */
  @Prop({ mutable: true, reflect: true }) errorType?: string;

  /**
   * Error message for input (set by form handler)
   */
  @Prop({ mutable: true, reflect: true }) errorMessage?: string;

  /**
   * Minimum length for input value
   */
  @Prop() minLength?: number;

  /**
   * Maximum length for input value
   */
  @Prop() maxLength?: number;

  /**
   * Custom message for minLength error
   */
  @Prop() minLengthMessage?: string;

  /**
   * Custom message for maxLength error
   */
  @Prop() maxLengthMessage?: string;

  @Prop({ mutable: true, reflect: true }) error?: string;

  @State() internalValue: string = '';

  @Event() valueInput: EventEmitter<string>;
  @Event() valueChange: EventEmitter<string>;
  @Event() clearFieldError: EventEmitter<string>;

  @Element() hostEl!: HTMLElement;

  componentWillLoad() {
    this.internalValue = this.value;
  }

  render() {
    const hasError = this.error === 'true';
    let inputId: string | undefined = undefined;
    if (this.label && this.name) {
      inputId = this.getResolvedName();
    }

    let errorText = this.errorMessage;
    if (!errorText && hasError) {
      const isEmailField = this.type === 'email';

      if (this.errorType === 'required') {
        if (isEmailField) {
          const constantValue = getConstant(this.constants, 'TEXT_FORM_MESSAGE_EMAIL_REQUIRED', 'Please enter your Email');
          errorText = this.requiredMessage || constantValue;
        } else {
          const constantValue = getConstant(this.constants, 'TEXT_FORM_MESSAGE_FIELD_REQUIRED', 'This field is required');
          errorText = this.requiredMessage || constantValue;
        }
      } else if (this.errorType === 'invalid') {
        if (isEmailField) {
          const constantValue = getConstant(this.constants, 'TEXT_FORM_MESSAGE_EMAIL_INVALID', 'This value seems to be invalid');
          errorText = this.invalidMessage || constantValue;
        } else {
          const constantValue = getConstant(this.constants, 'TEXT_FORM_MESSAGE_FIELD_IS_INVALID', 'Invalid value');
          errorText = this.invalidMessage || constantValue;
        }
      } else {
        const constantValue = getConstant(this.constants, 'TEXT_FORM_MESSAGE_FIELD_IS_INVALID', 'This field is invalid');
        errorText = constantValue;
      }
    }

    const resolvedName = this.getResolvedName();

    return (
      <div class={{ 'input-wrapper': true, 'has-error': hasError }}>
        {this.label && (
          <label htmlFor={inputId} class="input__label">
            {this.label}
          </label>
        )}
        <input
          class={{
            'input': true,
            'input--disabled': this.disabled,
            'input--error': hasError,
          }}
          {...(inputId ? { id: inputId } : {})}
          type={this.type}
          name={resolvedName}
          value={this.internalValue}
          placeholder={this.placeholder}
          disabled={this.disabled}
          required={this.required}
          minLength={this.minLength}
          maxLength={this.maxLength}
          onInput={this.handleInput}
          onChange={this.handleChange}
          onClick={this.handleClick}
          onFocus={this.handleFocus}
          autocomplete="off"
        />
        <me-text variant="small" class={`form-error-message ${hasError && errorText ? 'error-visible' : 'error-hidden'}`}>
          {errorText || '\u00A0'}
        </me-text>
      </div>
    );
  }

  private handleInput = (event: Event) => {
    const input = event.target as HTMLInputElement;
    this.internalValue = input.value;
    this.value = input.value;
    this.valueInput.emit(input.value);
    this.clearFieldError.emit(this.getResolvedName());
  };

  private handleChange = (event: Event) => {
    const input = event.target as HTMLInputElement;
    this.valueChange.emit(input.value);
    this.clearFieldError.emit(this.getResolvedName());
  };

  private handleClick = (event: MouseEvent) => {
    event.stopPropagation();
  };

  private handleFocus = () => {
    this.clearFieldError.emit(this.getResolvedName());
  };

  private getResolvedName(): string {
    // Find the nearest parent form with data-form-name attribute
    let parent = this.hostEl.parentElement;
    while (parent) {
      if (parent.tagName.toLowerCase() === 'form' && parent.hasAttribute('data-form-name')) {
        const formName = parent.getAttribute('data-form-name');
        if (formName && this.name) {
          return `${formName}-${this.name}`;
        }
      }
      parent = parent.parentElement;
    }
    return this.name;
  }
}
